import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, Trash2, Download, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * v260.18 — Renders the list of audio attachments captured via the
 * Transcript Importer. Each row exposes:
 *   - inline HTML5 <audio> playback (streams from the backend with
 *     auth cookies)
 *   - download link
 *   - delete button (super_admin or whoever uploaded it)
 *
 * Persists as `requirements_doc.attachments[]`; the actual bytes live
 * in MongoDB GridFS bucket `requirements_audio` keyed by attachment.id.
 */
export default function SourceRecordingsCard({
  estimateId, attachments, onAfterDelete,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const sorted = useMemo(() => {
    const arr = Array.isArray(attachments) ? [...attachments] : [];
    arr.sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
    return arr;
  }, [attachments]);

  if (!sorted.length) return null;

  const handleDelete = async (att) => {
    if (!window.confirm(`Delete "${att.filename}"? This removes the audio permanently.`)) return;
    setDeletingId(att.id);
    try {
      await axios.delete(
        `${API}/api/estimates/${estimateId}/requirements/attachments/${att.id}`,
        { withCredentials: true },
      );
      onAfterDelete?.(att.id);
      toast.success(`Deleted "${att.filename}"`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Delete failed';
      toast.error(detail);
    } finally {
      setDeletingId(null);
    }
  };

  const fmtSize = (b) => {
    if (!b) return '—';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return iso; }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
      data-testid="source-recordings-card"
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
        data-testid="source-recordings-toggle"
      >
        <div className="flex items-center gap-2">
          {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          <Mic className="w-4 h-4 text-cyan-600" />
          <span className="text-sm font-semibold text-slate-800">
            Source Recordings
          </span>
          <span className="text-[11px] text-slate-500">
            {sorted.length} audio file{sorted.length === 1 ? '' : 's'} captured via the Transcript Importer
          </span>
        </div>
      </button>
      {!collapsed && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {sorted.map((att) => {
            const streamUrl = `${API}/api/estimates/${estimateId}/requirements/attachments/${att.id}`;
            return (
              <div
                key={att.id}
                className="px-4 py-3 flex items-start gap-3"
                data-testid={`source-recording-${att.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-xs text-slate-800 truncate" title={att.filename}>
                      {att.filename}
                    </span>
                    <span className="text-[10px] text-slate-500 whitespace-nowrap">
                      {fmtSize(att.size_bytes)} · {fmtDate(att.uploaded_at)}
                    </span>
                  </div>
                  <audio
                    controls
                    preload="metadata"
                    src={streamUrl}
                    className="w-full h-8"
                    data-testid={`source-recording-audio-${att.id}`}
                  >
                    Your browser does not support inline audio playback.
                  </audio>
                  {att.uploaded_by && (
                    <div className="text-[10px] text-slate-500 mt-1">
                      Uploaded by {att.uploaded_by}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <a
                    href={streamUrl}
                    download={att.filename}
                    className="text-slate-500 hover:text-cyan-600 transition-colors p-1"
                    title="Download original audio"
                    data-testid={`source-recording-download-${att.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => handleDelete(att)}
                    disabled={deletingId === att.id}
                    className="text-slate-400 hover:text-red-600 transition-colors p-1 disabled:opacity-40"
                    title="Delete recording"
                    data-testid={`source-recording-delete-${att.id}`}
                  >
                    {deletingId === att.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
